import React, { useState, useEffect, useCallback, useRef } from "react";
import SubTitle from "components/SubTitle";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setReportId } from "store/slices/reportsSlice";
import { useLocation } from "react-router-dom";
import { useGetChatsListQuery } from "features/api/messages/messagesApiSlice";
import { Spinner } from "react-bootstrap";
import { LocalStorageKey, useGetUserData } from "common/helpers/userData";
import { UserRole } from "components/Reports/reportAssets/assets";
import {
  chatDataType,
  chatsQueryType,
  chatsProps,
  markChatAsSeenDataType,
} from "../../types";
import ChatContainer from "./ChatContainer";

const Chats = ({
  isReportContainerOpen,
  setIsOpen,
  setSelectedChat,
  selectedChat,
}: chatsProps) => {
  const { t } = useTranslation();
  const [chats, setChats] = useState<chatDataType[] | []>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [isEndOfList, setIsEndOfList] = useState(false);
  const chatsContainerRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const selectedRole = useGetUserData(LocalStorageKey.SelectedRole)?.value;
  const [controlCenterReportId, setControlCenterReportId] = useState(
    location?.state?.reportId
  );

  const {
    data: chatsList,
    isLoading,
    isFetching,
  }: {
    data: chatsQueryType;
    isLoading: boolean;
    isFetching: boolean;
  } = useGetChatsListQuery(
    {
      page: pageNumber,
      isControlCenter: selectedRole === UserRole.CONTROLCENTER,
      reportId: controlCenterReportId ?? null,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (selectedChat && controlCenterReportId !== selectedChat?.reportId) {
      // After using the reportId, set it to null
      setControlCenterReportId(null);
    }
  }, [controlCenterReportId, selectedChat]);

  useEffect(() => {
    setChats(chatsList?.chatsList);
    setIsEndOfList(chatsList?.isEndOfList);
  }, [chatsList]);

  useEffect(() => {
    if (!selectedChat && chats?.length > 0 && pageNumber === 1) {
      setSelectedChat(chats[0]);
      dispatch(setReportId(chats[0].reportId));
    }
  }, [chats, pageNumber, dispatch, setSelectedChat, selectedChat]);

  const handleScroll = useCallback(() => {
    const chatsContainer = chatsContainerRef.current;

    // Check if the user has reached the bottom of the page.
    if (
      !isEndOfList &&
      chatsContainer &&
      chatsContainer.clientHeight + chatsContainer.scrollTop ===
        chatsContainer.scrollHeight
    ) {
      // If the user reached the bottom and data is not already loading, load more data.
      if (!isFetching) {
        setPageNumber((prev) => prev + 1);
      }
    }
  }, [isFetching, isEndOfList]);

  useEffect(() => {
    // Add a scroll event listener to the chatsContainer to detect when the user has scrolled.
    const chatsContainer = chatsContainerRef.current;

    if (chatsContainer) {
      chatsContainer.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (chatsContainer) {
        chatsContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll, chatsList]);

  return (
    <div
      ref={chatsContainerRef}
      className="chatsContainer"
      style={{
        flex: isReportContainerOpen ? 0.5 : 1,
      }}
    >
      <SubTitle className="title">{t("messages")}</SubTitle>
      {isLoading ? (
        <div className="loader-container">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          {chats?.map((chat: chatDataType) => (
            <button
              type="button"
              key={chat.id}
              className="chatContainer"
              style={{
                backgroundColor:
                  chat.id === selectedChat?.id ? "#c2c4cd" : "transparent",
              }}
              onClick={() => {
                setSelectedChat(chat);
                setIsOpen(false);
                dispatch(setReportId(chat.reportId));
              }}
            >
              <ChatContainer chat={chat} />
            </button>
          ))}
          <div
            className="loading"
            style={{ visibility: isFetching ? "visible" : "hidden" }}
          >
            {t("loadingMore")}
          </div>
        </>
      )}
    </div>
  );
};

export default Chats;
